import { useCallback, useEffect, useMemo, useState } from "react";

export interface UsePagingResult<T> {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    pagedItems: Array<T>;
    goToPage: (page: number) => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    resetPaging: () => void;
    canGoNext: boolean;
    canGoPrevious: boolean;
}

export const usePaging = <T extends unknown>(props: { items: Array<T>; pageSize: number }): UsePagingResult<T> => {
    const { items, pageSize } = props;

    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.ceil(items.length / pageSize);

    const pagedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return items.slice(startIndex, endIndex);
    }, [items, currentPage, pageSize]);

    const goToPage = useCallback(
        (page: number) => {
            if (page >= 1 && page <= totalPages) {
                setCurrentPage(page);
            }
        },
        [totalPages],
    );

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const resetPaging = useCallback(() => {
        setCurrentPage(1);
    }, []);

    // scroll to top when page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentPage]);

    const canGoNext = currentPage < totalPages;
    const canGoPrevious = currentPage > 1;

    return {
        currentPage,
        totalPages,
        pageSize,
        pagedItems,
        goToPage,
        goToNextPage,
        goToPreviousPage,
        resetPaging,
        canGoNext,
        canGoPrevious,
    };
};
